import { Text as MantineText } from "@mantine/core";
import { useDebouncedState, useDebouncedValue, useHover, useMergedRef } from "@mantine/hooks";
import { polymorphicComponentWrapper } from "../../utilities";
import styles from "./text.module.scss";
import type { CustomTextFactory, IProps, ISkeletonProps, ITextCopyProps, ITextTooltipProps } from "./text.types";
import { CopyButton } from "../copy-button";
import { Tooltip } from "../tooltip";
import classNames from "classnames";
import { Icon } from "../icon";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";
import { Group } from "../group";
import { Skeleton } from "../skeleton";
import { useMemo, useRef } from "react";
import { useIsOverflowed, decodeHtmlEntities } from "@edgetier/utilities";

/**
 * Text component for displaying text content with optional styling.
 * @param {boolean} props.dimmed - Whether the text should appear dimmed.
 * @param {boolean} props.noWrap - Whether the text should not wrap to a new line.
 * @returns {JSX.Element} The Text component.
 */
const Text = polymorphicComponentWrapper<CustomTextFactory, IProps>("Text", ({ dimmed, noWrap, ...props }, ref) => {
    const children = useMemo(
        () => (typeof props.children === "string" ? decodeHtmlEntities(props.children) : props.children),
        [props.children]
    );

    return (
        <MantineText
            data-dimmed={dimmed}
            data-no-wrap={noWrap}
            ref={ref}
            classNames={{ root: styles["root"] }}
            {...props}
        >
            {children}
        </MantineText>
    );
});

/**
 * Skeleton loader component for Text.
 * @returns {JSX.Element} The TextSkeleton component.
 */
const TextSkeleton = (props: ISkeletonProps) => {
    return <Skeleton className={styles["skeleton"]} {...props} />;
};

/**
 * Text component with copy functionality that allows users to copy the text value.
 * @param {string} props.value - The text value to be copied.
 * @param {number} props.timeout - Timeout in milliseconds for showing the copy tooltip.
 * @param {boolean} props.disableCopy - Whether to disable the copy functionality.
 * @param {string} props.className - Additional CSS class names.
 * @returns {JSX.Element} The TextCopy component.
 */
const TextCopy = ({ value, timeout, disableCopy, className, ...textProps }: ITextCopyProps) => {
    const { hovered, ref } = useHover<HTMLParagraphElement>();
    const [debouncedOpen] = useDebouncedValue(hovered, timeout ?? 900);
    const [debouncedCopied, setDebouncedCopied] = useDebouncedState(false, 200);

    return disableCopy ? (
        <Text {...textProps} />
    ) : (
        <CopyButton value={value} timeout={timeout}>
            {({ copied, copy }) => {
                setDebouncedCopied(copied);
                return (
                    // The group never wraps and the icon is only ever hidden (not unmounted), so a long
                    // value can neither push the icon onto its own line nor reflow when hovered.
                    // A long value wraps within the row, so the icon is pinned to its first line
                    // rather than floating against the middle of the wrapped block.
                    <Group justify="space-between" wrap="nowrap" align="flex-start" flex={1}>
                        <Text
                            ref={ref}
                            onClick={copy}
                            td={hovered ? "dotted" : undefined}
                            c={hovered ? "blue" : undefined}
                            flex={1}
                            miw={0}
                            className={classNames(styles["copy"], className)}
                            {...textProps}
                        />
                        <Tooltip
                            opened={(hovered && debouncedOpen) || copied}
                            label={copied || debouncedCopied ? "Copied" : "Copy"}
                        >
                            <Icon
                                size="xxs"
                                color={copied || debouncedCopied ? "teal" : "gray"}
                                icon={copied || debouncedCopied ? faCheck : faCopy}
                                className={classNames(styles["copy-icon"], {
                                    [styles["copy-icon--visible"]]: hovered || copied,
                                })}
                            />
                        </Tooltip>
                    </Group>
                );
            }}
        </CopyButton>
    );
};

/**
 * Text component with a tooltip that displays when hovered over the text and the text is overflowed or given another condition.
 * @param props.tooltipProps - The props for the Tooltip component.
 * @param props.props - The props for the Text component.
 * @returns
 */
const TextTooltip = polymorphicComponentWrapper<CustomTextFactory, ITextTooltipProps>(
    "TextTooltip",
    ({ tooltipProps: { disabled, ...tooltipProps }, ...props }, ref) => {
        const textRef = useRef<HTMLParagraphElement>(null);
        const isHeightOverflowed = useIsOverflowed(textRef, "height");
        const isWidthOverflowed = useIsOverflowed(textRef, "width");

        const mergedRef = useMergedRef(ref, textRef);
        return (
            <Tooltip {...tooltipProps} disabled={disabled || (!isHeightOverflowed && !isWidthOverflowed)}>
                <Text ref={mergedRef} noWrap={false} {...props} />
            </Tooltip>
        );
    }
);

Text.Skeleton = TextSkeleton;
Text.Copy = TextCopy;
Text.Tooltip = TextTooltip;

export { Text, TextSkeleton, TextCopy, TextTooltip };
